import React, { useEffect, useMemo, useRef, useState } from 'react';

// MUI
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';

import PlusOutlined from '@ant-design/icons/PlusOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';

import MainCard from 'components/MainCard';
import api from 'api/axios';

// ==============================|| Confirm Hook ||============================== //
function useConfirm() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const payloadRef = useRef({
    title: '',
    body: '',
    confirmText: 'Confirm',
    isDestructive: false,
    onConfirm: null
  });

  const ask = ({ title, body, onConfirm, confirmText = 'Confirm', isDestructive = false }) => {
    payloadRef.current = { title, body, onConfirm, confirmText, isDestructive };
    setOpen(true);
  };

  const close = () => {
    if (!submitting) setOpen(false);
  };

  const ConfirmDialog = (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ExclamationCircleOutlined />
        {payloadRef.current.title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ opacity: 0.85, whiteSpace: 'pre-wrap' }}>
          {payloadRef.current.body}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color={payloadRef.current.isDestructive ? 'error' : 'primary'}
          variant="contained"
          disabled={submitting}
          onClick={async () => {
            const fn = payloadRef.current.onConfirm;
            if (!fn) return;
            try {
              setSubmitting(true);
              await fn();
              setOpen(false);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting ? 'Processing...' : payloadRef.current.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { ask, ConfirmDialog };
}

// ==============================|| Pagination Bar ||============================== //
function PaginationBar({ totalCount, currentPage, totalPages, rowsPerPage, setCurrentPage, setRowsPerPage }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ opacity: 0.8 }}>
        Total: {totalCount} | Page {currentPage} / {totalPages}
      </Typography>

      <Box sx={{ flex: 1 }} />

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Button size="small" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1 || totalCount === 0}>
          Prev
        </Button>
        <Button
          size="small"
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage >= totalPages || totalCount === 0}
        >
          Next
        </Button>

        <Select
          size="small"
          value={rowsPerPage}
          onChange={(e) => {
            setCurrentPage(1);
            setRowsPerPage(Number(e.target.value));
          }}
          sx={{ minWidth: 140 }}
        >
          {[10, 25, 50, 100].map((n) => (
            <MenuItem key={n} value={n}>
              {n} / page
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </Stack>
  );
}

// ==============================|| Status Chip ||============================== //
function StatusChip({ active }) {
  return <Chip size="small" label={active ? 'Active' : 'Inactive'} color={active ? 'success' : 'error'} variant="filled" />;
}

// ==============================|| Page ||============================== //
export default function Whitelist() {
  const AUTH = useMemo(
    () => ({
      withCredentials: true,
      headers: { 'X-API-Key': 'secret123' }
    }),
    []
  );

  // table & pagination
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // add modal
  const [addOpen, setAddOpen] = useState(false);
  const [ipAdd, setIpAdd] = useState('');

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [ipEdit, setIpEdit] = useState('');
  const [activeEdit, setActiveEdit] = useState(true);

  const { ask, ConfirmDialog } = useConfirm();

  const counts = useMemo(() => {
    const active = rows.filter((r) => r.whitelist_status).length;
    return { active, total: rows.length };
  }, [rows]);

  const fetchRows = async () => {
    try {
      setLoading(true);

      // GET /api/whitelist/all/:limit/:page
      const res = await api.get(`/api/whitelist/all/${rowsPerPage}/${currentPage}`, AUTH);

      // response: { limit, page, total, whitelist: [...] }
      const payload = res?.data || {};
      const list = Array.isArray(payload.whitelist) ? payload.whitelist : [];

      setRows(
        list.map((d) => ({
          whitelist_id: d.id,
          whitelist_ip: d.ip,
          whitelist_status: !!d.active,
          whitelist_created_at: d.created_at,
          whitelist_created_by: d.created_by,
          whitelist_edited_at: d.updated_at,
          whitelist_edited_by: d.updated_by
        }))
      );

      const limit = Number(payload.limit) || rowsPerPage;
      const total = Number(payload.total) || 0;

      setTotalCount(total);
      setTotalPages(Math.max(1, Math.ceil(total / limit)));
    } catch (err) {
      console.error('Failed to fetch whitelist:', err);
      setRows([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage]);

  const addWhitelist = async () => {
    const ip = ipAdd.trim();
    if (!ip) return;

    // POST /api/whitelist { ip }
    await api.post('/api/whitelist', { ip }, AUTH);

    setAddOpen(false);
    setIpAdd('');
    fetchRows();
  };

  const openEdit = (row) => {
    setEditId(row.whitelist_id);
    setIpEdit(row.whitelist_ip);
    setActiveEdit(!!row.whitelist_status);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editId) return;

    // PUT /api/whitelist/:id { ip, active }
    await api.put(`/api/whitelist/${editId}`, { ip: ipEdit.trim(), active: activeEdit }, AUTH);

    setEditOpen(false);
    fetchRows();
  };

  const toggleStatus = async (row) => {
    // PUT /api/whitelist/:id/status { active }
    await api.put(`/api/whitelist/${row.whitelist_id}/status`, { active: !row.whitelist_status }, AUTH);

    setRows((prev) => prev.map((r) => (r.whitelist_id === row.whitelist_id ? { ...r, whitelist_status: !r.whitelist_status } : r)));
  };

  const removeRow = async (row) => {
    // DELETE /api/whitelist/:id
    await api.delete(`/api/whitelist/${row.whitelist_id}`, AUTH);
    setRows((prev) => prev.filter((r) => r.whitelist_id !== row.whitelist_id));
    fetchRows();
  };

  return (
    <Box sx={{ p: 2 }}>
      <MainCard
        content={false}
        title={
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <Typography variant="h6">IP Whitelist</Typography>
            <Chip size="small" variant="outlined" label={`${counts.active}/${counts.total} Active on this page`} sx={{ ml: 1 }} />
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" startIcon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
              Add Whitelist
            </Button>
          </Stack>
        }
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  {['ID', 'IP ADDRESS', 'STATUS', 'ACTIONS'].map((h) => (
                    <Box
                      key={h}
                      component="th"
                      sx={{
                        textAlign: 'left',
                        fontWeight: 700,
                        p: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box component="tbody">
                {rows.length === 0 ? (
                  <Box component="tr">
                    <Box component="td" colSpan={4} sx={{ p: 1, opacity: 0.75 }}>
                      {loading ? 'Loading...' : 'Belum ada data.'}
                    </Box>
                  </Box>
                ) : (
                  rows.map((r) => (
                    <Box component="tr" key={r.whitelist_id}>
                      <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        {r.whitelist_id}
                      </Box>

                      <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {r.whitelist_ip}
                        </Typography>
                      </Box>

                      <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <StatusChip active={r.whitelist_status} />
                      </Box>

                      <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            size="small"
                            variant={r.whitelist_status ? 'outlined' : 'contained'}
                            onClick={() =>
                              ask({
                                title: `${r.whitelist_status ? 'Nonaktifkan' : 'Aktifkan'} whitelist?`,
                                body: r.whitelist_ip,
                                confirmText: r.whitelist_status ? 'Set Inactive' : 'Set Active',
                                onConfirm: () => toggleStatus(r)
                              })
                            }
                          >
                            {r.whitelist_status ? 'Set Inactive' : 'Set Active'}
                          </Button>

                          <Button size="small" variant="outlined" startIcon={<EditOutlined />} onClick={() => openEdit(r)}>
                            Edit
                          </Button>

                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteOutlined />}
                            onClick={() =>
                              ask({
                                title: 'Hapus whitelist?',
                                body: r.whitelist_ip,
                                confirmText: 'Hapus',
                                isDestructive: true,
                                onConfirm: () => removeRow(r)
                              })
                            }
                          >
                            Delete
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </MainCard>

      {/* Pagination OUTSIDE the card */}
      <PaginationBar
        totalCount={totalCount}
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        setCurrentPage={setCurrentPage}
        setRowsPerPage={setRowsPerPage}
      />

      {/* ADD Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Whitelist</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <TextField label="Whitelist IP" placeholder="e.g. 203.0.113.5" value={ipAdd} onChange={(e) => setIpAdd(e.target.value)} />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const ip = ipAdd.trim();
              if (!ip) return;
              ask({
                title: 'Tambah whitelist?',
                body: ip,
                confirmText: 'Tambah',
                onConfirm: addWhitelist
              });
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Whitelist</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Whitelist IP" value={ipEdit} onChange={(e) => setIpEdit(e.target.value)} />
            <FormControlLabel control={<Switch checked={activeEdit} onChange={(e) => setActiveEdit(e.target.checked)} />} label="Active" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() =>
              ask({
                title: 'Simpan perubahan?',
                body: ipEdit,
                confirmText: 'Simpan',
                onConfirm: saveEdit
              })
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {ConfirmDialog}
    </Box>
  );
}
